import { APP_NAME } from '@sellit/shared';

/** "Sat, Nov 7" in the sale's own timezone. */
export function formatDeadline(iso: string | null, timeZone: string): string | null {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone }).format(new Date(iso));
  } catch {
    return new Date(iso).toLocaleDateString();
  }
}

export const STATUS_LABEL = { AVAILABLE: 'Available', PENDING: 'Pending', SOLD: 'Sold', ARCHIVED: 'Archived' } as const;

export function setPageTitle(title: string | undefined) {
  if (typeof document !== 'undefined' && title) document.title = title;
}

/** Title for the app's own screens: "Add item · VenLink". */
export function setAppTitle(title: string) {
  setPageTitle(`${title} · ${APP_NAME}`);
}

/** localStorage that never throws (private mode, blocked storage). */
export const storage = {
  get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string) {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* ignore */
    }
  },
  remove(key: string) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

/** "just now", "5 min ago", "3 hr ago", else a short date. */
export function timeAgo(iso: string, now = Date.now()): string {
  const mins = Math.round((now - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 24 * 60) return `${Math.round(mins / 60)} hr ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Cents to the plain number a price box shows: 4000 -> "40", 1250 -> "12.50". */
export function centsToInput(cents: number | null): string {
  if (cents === null) return '';
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
}

export const SALE_STATUS_LABEL = { DRAFT: 'Draft', ACTIVE: 'Published', CLOSED: 'Closed', ARCHIVED: 'Archived' } as const;

/** One line per sale status, for the status picker. */
export const SALE_STATUS_HELP = {
  DRAFT: 'Hidden. Only you and its sellers can preview it.',
  ACTIVE: 'Live. Anyone with the link can browse and send inquiries.',
  CLOSED: 'Still visible, marked as ended. No new inquiries.',
  ARCHIVED: 'Hidden again. Kept for the record.',
} as const;

/** Copies text; falls back to a prompt where the clipboard API is blocked. */
export async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    window.prompt('Copy this:', text);
  }
}

/** Turns a title into a suggested URL slug: "The Cox Family Sale!" -> "the-cox-family-sale". */
export function slugify(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/, '');
}

export const TIMEZONES = [
  ['America/New_York', 'Eastern'],
  ['America/Chicago', 'Central'],
  ['America/Denver', 'Mountain'],
  ['America/Phoenix', 'Arizona'],
  ['America/Los_Angeles', 'Pacific'],
  ['America/Anchorage', 'Alaska'],
  ['Pacific/Honolulu', 'Hawaii'],
] as const;
